import produce from 'immer';

import {
  ADD_FILTER,
  SET_FILTER,
  SET_FILTER_OPERATOR,
  SET_FILTER_MODEL_OPERATOR,
  UNSET_FILTER,
  CLEAR_FILTERS,
  SET_ACCOUNT
} from 'app/actions/types';

import {
  ME
} from 'shared/actions/types';

import {
  OPERATOR_KEY,
  AND_OPERATOR,
  OR_OPERATOR
} from 'app/hooks/useFilter';

import {
  PAGE_SIZE
} from 'app/utils/Constants';

const initialFilterModel = {
  [OPERATOR_KEY]: [{
    operator: AND_OPERATOR,
    value: AND_OPERATOR,
    filterClass: '',
    filterType: ''
  }]
}

const initialState = {
  page: 1,
  per_page: PAGE_SIZE,
  filter_model: initialFilterModel,
  sort_model: [],
  account_id: null
}

// helper function to set filter attributes
const setFilter = (state, field, filterIndex, attrs) => {
  state.filter_model[field] ||= [];
  state.filter_model[field][filterIndex] ||= {};
  state.filter_model[field][filterIndex] = {
    ...state.filter_model[field][filterIndex],
    ...attrs
  }
}

const paramsReducer = (resource, setAction) => {
  return (state = initialState, action) => {
    // check if this is the set action (ex: SET_JOBS).
    // this takes the GET API response and sets it in Redux
    if (action.type === setAction) {
      return produce(state, draft => {
	const {
          page,
          per_page,
          sort_model,
          filter_model,
          account_id
	} = action.payload;

	draft.page = page;
	draft.per_page = per_page;
	draft.sort_model = sort_model;
	draft.filter_model = filter_model;
        draft.account_id = account_id;
      });
    }

    // set account ID when account is set
    if (action.type === ME && !state.account_id && action.payload?.account) {
      return produce(state, draft => {
        draft.account_id = action.payload.account.id;
      });
    }

    // if switching account and filters are set then reset params
    if (action.type === SET_ACCOUNT) {
      let filterCount = Object.keys(state.filter_model)
        .filter(key => key !== OPERATOR_KEY).length;

      if (filterCount === 0 || state.account_id === action.payload.id) {
        return state;
      }

      // reset params
      return produce(state, draft => {
        for (let param in initialState) {
          draft[param] = initialState[param];
        }

        draft.account_id = action.payload.id;
      });
    }

    // if it's not the set action, then the resource is ambiguous.
    // if the resource doesn't match then ignore it.
    // i.e. the below actions will only be caught if the resource is passed
    // (the filter actions pass the resource to work here)
    if (action.payload?.resource !== resource) {
      return state;
    }

    switch (action.type) {
      case setAction:
        return set(state, action.payload);

      case ADD_FILTER:
        return produce(state, draft => {
	  const {
	    field,
	    filterClass,
	    filterType
	  } = action.payload;

	  // if addFilter has been called,
	  // we can assume that at least one filter row is already present.
	  // if this row isn't in the filter model,
	  // then that means the filter is still blank.
	  // in this case an empty object should be added as a placeholder
	  draft.filter_model[field] ||= [{}];

	  // add the new row (the remaining attributes will be set by the filter)
	  draft.filter_model[field].push({
	    filterClass,
	    filterType,
	    operator: AND_OPERATOR
	  });
	});

      case SET_FILTER:
        return produce(state, draft => {
          const {
	    field,
	    filterIndex,
	    filterClass,
	    filterType,
	    value,
	    operator
	  } = action.payload;

	  setFilter(draft, field, filterIndex, {
	    filterClass,
	    filterType,
	    value,
	    operator
	  });
	});

      case SET_FILTER_OPERATOR:
        return produce(state, draft => {
          const {
	    field,
	    filterIndex,
	    operator
	  } = action.payload;

	  setFilter(draft, field, filterIndex, {
	    operator
	  });
	});

      case SET_FILTER_MODEL_OPERATOR:
        return produce(state, draft => {
          const {
	    operator
	  } = action.payload;

	  setFilter(draft, OPERATOR_KEY, 0, {
	    value: operator,
	    operator: operator,
	  });
	});

      case UNSET_FILTER:
        return produce(state, draft => {
          const {
	    field,
	    filterIndex
	  } = action.payload;

	  draft.filter_model[field] ||= [];

	  // remove filter from field's filter array
	  if (draft.filter_model[field][filterIndex]) {
	    draft.filter_model[field].splice(filterIndex, 1);
	  }

	  // if there are no more filters in the array then delete the field
	  if (!draft.filter_model[field].length) {
	    delete draft.filter_model[field];
	  }
	});

      case CLEAR_FILTERS:
	return produce(state, draft => {
          draft.filter_model = initialFilterModel;
	});

      default:
        return state;
    }
  }

}

export default paramsReducer;
